import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Compass } from 'lucide-react';
import { useJourney } from '../context/JourneyContext';

// Official Let's Siva logo
const WHITE_LOGO = "https://customer-assets.emergentagent.com/job_dance-confidence/artifacts/00kbs5d6_Lets%20Siva_Final%20Logo_Black.png";

// YouTube video ID
const YOUTUBE_VIDEO_ID = "aXDBb993xD4";

// Let's Siva highlight video with Play Overlay (YouTube)
const LSIntroVideo = () => {
  const [isPlaying, setIsPlaying] = useState(false);

  const src = useMemo(() => {
    const base = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`;
    const paramsWhenPlaying = "autoplay=1&playsinline=1&rel=0&modestbranding=1";
    const paramsIdle = "playsinline=1&rel=0&modestbranding=1";
    return isPlaying ? `${base}?${paramsWhenPlaying}` : `${base}?${paramsIdle}`;
  }, [isPlaying]);

  return (
    <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
      {/* Video title - both lines highlighted */}
      <p className="text-sm font-extrabold text-stone-200 text-center mb-1" style={{ opacity: 0.95 }}>
        Highlight video: Papatoetoe East Primary School (PEPS)
      </p>
      
      <p className="text-sm font-bold text-stone-300 text-center mb-3" style={{ opacity: 0.9 }}>
        One of our long-term partner schools.
      </p>

      {/* Responsive 16:9 video container */}
      <div 
        className="relative w-full max-w-lg mx-auto rounded-2xl overflow-hidden border border-stone-700"
        style={{ 
          background: 'rgba(255,255,255,0.06)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
        }}
      >
        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
          {!isPlaying ? (
            // Play overlay
            <button
              type="button"
              onClick={() => setIsPlaying(true)}
              aria-label="Play Let's Siva highlight video"
              className="absolute inset-0 w-full h-full border-0 cursor-pointer p-0 flex items-center justify-center transition-all duration-300"
              style={{
                background: 'radial-gradient(120% 120% at 50% 30%, rgba(255,255,255,0.10) 0%, rgba(0,0,0,0.40) 60%, rgba(0,0,0,0.60) 100%)',
              }}
            >
              <div className="text-center p-4">
                {/* Play button circle */}
                <div 
                  className="w-20 h-20 rounded-full inline-flex items-center justify-center transition-transform duration-300 hover:scale-110"
                  style={{
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.22)',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  {/* Triangle play icon */}
                  <div 
                    className="ml-1"
                    style={{
                      width: 0,
                      height: 0,
                      borderTop: '12px solid transparent',
                      borderBottom: '12px solid transparent',
                      borderLeft: '18px solid rgba(255,255,255,0.92)',
                    }}
                  />
                </div>
                
                <p className="text-white text-sm mt-3" style={{ opacity: 0.85 }}>
                  Tap to play (1:34)
                </p>
              </div>
            </button>
          ) : (
            // YouTube iframe loads only after click
            <iframe
              src={src}
              title="Let's Siva highlight video (PEPS)"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 w-full h-full border-0"
            />
          )}
        </div>
      </div>

      <p className="text-xs text-stone-500 mt-3 text-center" style={{ opacity: 0.7 }}>
        Questions?{' '}
        <a href="mailto:info@letssiva.com" className="text-primary hover:underline">
          info@letssiva.com
        </a>
      </p>
    </div>
  );
};

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { createJourney, trackPageView } = useJourney();

  React.useEffect(() => {
    trackPageView('welcome');
  }, [trackPageView]);

  const handleStart = async () => {
    await createJourney({});
    navigate('/choose-path');
  };

  const handleBrowse = () => {
    navigate('/browse');
  };

  return (
    <div 
      data-testid="welcome-screen"
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6 py-12"
    >
      {/* Background - solid dark */}
      <div className="absolute inset-0 bg-[#1C1917]" />
      
      {/* Subtle decorative blobs */}
      <div className="absolute top-0 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 -right-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4">
        {/* Official Logo */}
        <div className="mb-2 animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
          <div className="relative inline-block">
            <img 
              src={WHITE_LOGO}
              alt="Let's Siva"
              className="h-24 sm:h-32 mx-auto object-contain"
              data-testid="main-logo"
            />
            <span
              style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                marginLeft: 4,
                textShadow: "0 1px 6px rgba(0,0,0,0.8)",
                position: "absolute",
                top: 8,
                right: -16,
              }}
            >
              ™
            </span>
          </div>
        </div>

        {/* Merged intro line */}
        <p 
          data-testid="welcome-subtext"
          className="text-lg sm:text-xl text-stone-300 max-w-lg mx-auto font-medium animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.2s', animationFillMode: 'forwards', opacity: 0.92 }}
        >
          We'll guide you to the right pathway in under 60 seconds.
        </p>

        {/* Video Section */}
        <LSIntroVideo />

        {/* CTAs */}
        <div 
          className="flex flex-col sm:flex-row gap-4 justify-center pt-3 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.45s', animationFillMode: 'forwards' }}
        >
          <Button
            data-testid="start-journey-btn"
            onClick={handleStart}
            size="lg"
            className="rounded-full px-8 py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-105 active:scale-95"
          >
            Start
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          
          <Button
            data-testid="browse-btn"
            onClick={handleBrowse}
            variant="ghost"
            size="lg"
            className="rounded-full px-8 py-6 text-lg font-medium text-stone-300 hover:text-white hover:bg-white/10 transition-all duration-300"
            style={{ opacity: 0.9 }}
          >
            <Compass className="mr-2 w-5 h-5" />
            Browse
          </Button>
        </div>

        {/* Tagline - moved to bottom */}
        <p 
          className="font-accent text-sm sm:text-base text-stone-300 pt-4 animate-fade-in-up opacity-0"
          style={{ animationDelay: '0.55s', animationFillMode: 'forwards', opacity: 0.8 }}
        >
          Confidence. Connection. Fun. Safe spaces — through movement.
        </p>
      </div>
    </div>
  );
};

export default WelcomeScreen;
